import { LoadingButton } from '@mui/lab';
import {
  Box,
  Grid,
  IconButton,
  // styled,
  TextField,
  Stack,
  Typography,
  Paper,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import { Transition } from '../transition';
import { useFormik } from 'formik';
import { ApiEndpoint, AppColors, config } from '../../utils/utils';
import bookDemoFormSchema from '../../validation/bookDemoFormSchema';
import {
  getCapitalizedString,
  showSuccessMessage,
} from '../../utils/utilMethods';
import { axiosInstance } from '../../client';
import VideoModel from './VideoModel';
import { useEffect, useState } from 'react';
import useToggle from '../../hooks/useToggle';

// const BannerDialogTitle = styled('div')(({ theme }) => ({
//   backgroundImage: `url(/static/images/book_demo_header_img.png)`,
//   backgroundSize: 'cover',
//   backgroundPosition: 'center',
//   position: 'relative',
//   padding: theme.spacing(3),
//   color: '#fff',
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'space-between',
//   minHeight: '10vh',
//   [theme.breakpoints.up('xs')]: {
//     fontSize: '1.5rem',
//   },
//   [theme.breakpoints.up('md')]: {
//     fontSize: '2rem',
//   },
// }));

interface Feature {
  iconPath?: string;
  title: string;
}

const featureList: Feature[] = [
  {
    iconPath: '/static/images/list_1.png',
    title: 'Instantly capture and track leads in real time',
  },
  {
    iconPath: '/static/images/list_2.png',
    title: 'Empower your sales team with SmartLink technology',
  },
  {
    iconPath: '/static/images/list_3.png',
    title: 'Gain visibility with real-time pipeline analytics',
  },
  {
    iconPath: '/static/images/list_4.png',
    title: 'Close more deals with automated follow-up triggers',
  },
];

interface IBookDemoFormState {
  first_name: string;
  last_name: string;
  name: string;
  dealership_name: string;
  location: string;
  position: string;
  no_of_sales_people: number;
  office_number: string;
  office_number_ext?: string;
  office_email: string;
  mobile_number?: string;
  member_of_dealer_group: number;
  contact_time?: string;
  sales_rep?: string;
}

interface IProps {
  toggle: boolean;
  onClose: () => void;
  onSubmitSuccess?: (code: string) => void;
}

const BookDemoFormModel: React.FC<IProps> = ({
  toggle,
  onClose,
  onSubmitSuccess,
}) => {
  const [videoUrl, setVideoUrl] = useState<string | undefined>();
  // const [isPlayIntroVideo] = useState<boolean>(false);

  const {
    toggle: toggleVideo,
    onOpen: onOpenVideo,
    onClose: onCloseVideo,
  } = useToggle();

  const inputFieldLabelStyle = {
    color: AppColors.blackColor,
    mb: 0.5,
  };

  const textFieldStyle = {
    '& label': {
      color: AppColors.grayThreeColor,
    },
    '& .MuiOutlinedInput-root input': {
      color: AppColors.blackColor,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: ` ${AppColors.grayOneColor} !important`,
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: `${AppColors.primaryColor} !important`,
    },
    '& .MuiInputLabel-shrink': {
      backgroundColor: AppColors.grayZeroColor,
      paddingLeft: '5px',
      paddingRight: '5px',
      borderRadius: '5px',
    },
    '& .MuiOutlinedInput-root': {
      // background: AppColors.grayZeroColor,
      '& fieldset': {
        borderColor: AppColors.grayOneColor,
      },
      '&:hover fieldset': {
        borderColor: AppColors.grayOneColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: AppColors.grayOneColor,
      },
    },
  };

  const {
    handleSubmit,
    values,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
  } = useFormik<IBookDemoFormState>({
    initialValues: {
      first_name: '',
      last_name: '',
      name: '',
      dealership_name: '',
      location: '',
      position: '',
      member_of_dealer_group: 0,
      office_number: '',
      office_number_ext: '',
      office_email: '',
      mobile_number: '',
      no_of_sales_people: 0,
      contact_time: '',
      sales_rep: '',
    },
    validationSchema: bookDemoFormSchema,
    onSubmit: async (values) => {
      try {
        const response = await axiosInstance.post(ApiEndpoint.REQUEST_DEMO, {
          ...{ ...values, name: `${values.first_name} ${values.last_name}` },
        });

        showSuccessMessage(`Thanks for your interest. An agent will be in touch with you shortly.`);

        if (response && response.data && response.data.data) {
          const sc = response.data.data.contact.contact_code;

          onSubmitSuccess && onSubmitSuccess(sc);
        }
        onClose();
        resetForm();
      } catch (error: any) {
        console.error(error);
      }
    },
  });

  useEffect(() => {
    if (toggleVideo === false) {
      setVideoUrl(undefined);
    }
  }, [toggleVideo]);

  const handlePlayVideo = (videoUrl: string | undefined) => {
    if (videoUrl !== undefined) {
      setVideoUrl(videoUrl);
      onOpenVideo();
    }
  };

  const handleCallToAssistant = () => {
    let telLink = `tel:${config.AUTO_ASSISTANT_CONTACT_NUMBER}`;
    window.open(`${telLink}`, '_blank') ||
      window.location.replace(`${telLink}`);
  };

  return (
    <Dialog
      maxWidth='lg'
      fullWidth
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => {
        resetForm();
        onClose();
      }}
      aria-describedby='alert-dialog-slide-description'
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '12px', // Set your desired radius here
        },
      }}
    >
      <IconButton
        aria-label='close'
        onClick={() => {
          resetForm();
          onClose();
        }}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          color: 'white',
          backgroundColor: 'red',
          '&:hover': {
            backgroundColor: 'rgba(255,0,0,0.7)',
          },
        }}
      >
        <CloseIcon fontSize='small' />
      </IconButton>
      {/* <BannerDialogTitle
        sx={{
          color: AppColors.primaryColor,
          textAlign: { md: 'start', xs: 'center' },
        }}
      >
        Book a Demo
      </BannerDialogTitle> */}
      <form onSubmit={handleSubmit} autoComplete='off'>
        <DialogContent
          sx={{
            padding: '24px',
            background: `url('/static/home_page/knocard_auto_banner_small.png')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        >
          <Grid container justifyContent={'center'} gap={3}>
            <Grid size={{ xs: 12, sm: 12, md: 5.8 }} alignContent={'center'}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',

                  color: 'white',
                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 'clamp(1.25rem, 3vw, 2rem)', // h3 size

                    fontWeight: 'bold',
                  }}
                >
                  Drive More Sales with KnoCard Auto
                </Typography>
                <Typography
                  sx={{
                    fontSize: 'clamp(0.875rem, 2vw, 1.25rem)', // h5 size
                  }}
                >
                  In less than 30 minutes, you'll discover how to...
                </Typography>

                <Box>
                  {featureList.map((item, index) => {
                    return (
                      <Stack
                        key={index}
                        direction='row'
                        alignItems='start'
                        py={1}
                      >
                        <Box
                          component='img'
                          src={item.iconPath}
                          height='24px'
                          width='24px'
                        />
                        <Typography sx={{ pl: 1 }}>{item.title}</Typography>
                      </Stack>
                    );
                  })}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <Box
                    component='img'
                    src='/static/home_page/book_demo_thumbnail.png'
                    alt='video thumbnail'
                    sx={{ width: { xs: '90%', sm: '60%' } }}
                    // onClick={handleCallToAssistant}
                  />

                  <Box
                    sx={{
                      width: '100%',
                      height: '20%',
                      position: 'absolute',
                      bottom: 0,
                      cursor: 'pointer',
                    }}
                    onClick={handleCallToAssistant}
                  />

                  <Box
                    component='img'
                    src='/static/images/ic_play_button.svg'
                    alt='Play'
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      cursor: 'pointer',
                      transform: 'translate(-50%, -50%)',
                      width: { xs: '80px', md: '100px' },
                      height: { xs: '80px', md: '100px' },
                    }}
                    onClick={() => {
                      handlePlayVideo(
                        'https://storage-prod.knocard.net/knocard_auto_brand_consultant/Book_Demo.MP4'
                      );
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 5.8 }}>
              <Paper sx={{ p: 2 }}>
                <Stack direction='column' spacing={2}>
                  <Grid container gap={2} justifyContent={'center'}>
                    <Grid size={{ xs: 12, md: 5.8 }}>
                      <Typography sx={inputFieldLabelStyle}>
                        First Name*
                      </Typography>
                      <TextField
                        id='first_name'
                        name='first_name'
                        fullWidth
                        onChange={handleChange}
                        value={values.first_name}
                        onInput={(e: any) => {
                          e.target.value = getCapitalizedString(
                            e.target.value ?? ''
                          );
                        }}
                        error={Boolean(errors.first_name && touched.first_name)}
                        helperText={
                          Boolean(errors.first_name && touched.first_name) &&
                          errors.first_name
                        }
                        sx={{ ...textFieldStyle }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 5.8 }}>
                      <Typography sx={inputFieldLabelStyle}>
                        Last Name*
                      </Typography>
                      <TextField
                        id='last_name'
                        name='last_name'
                        fullWidth
                        onChange={handleChange}
                        value={values.last_name}
                        onInput={(e: any) => {
                          e.target.value = getCapitalizedString(
                            e.target.value ?? ''
                          );
                        }}
                        error={Boolean(errors.last_name && touched.last_name)}
                        helperText={
                          Boolean(errors.last_name && touched.last_name) &&
                          errors.last_name
                        }
                        sx={{ ...textFieldStyle }}
                      />
                    </Grid>
                  </Grid>

                  <Box>
                    <Typography sx={inputFieldLabelStyle}>
                      Dealership Name*
                    </Typography>
                    <TextField
                      id='dealership_name'
                      name='dealership_name'
                      fullWidth
                      onChange={handleChange}
                      value={values.dealership_name}
                      onInput={(e: any) => {
                        e.target.value = getCapitalizedString(
                          e.target.value ?? ''
                        );
                      }}
                      error={Boolean(
                        errors.dealership_name && touched.dealership_name
                      )}
                      helperText={
                        Boolean(
                          errors.dealership_name && touched.dealership_name
                        ) && errors.dealership_name
                      }
                      sx={{ ...textFieldStyle }}
                    />
                  </Box>

                  <Box>
                    <Typography sx={inputFieldLabelStyle}>State*</Typography>
                    <TextField
                      type='text'
                      name='location'
                      id='location'
                      fullWidth
                      value={values.location}
                      onInput={(e: any) => {
                        e.target.value = getCapitalizedString(
                          e.target.value ?? ''
                        );
                      }}
                      onChange={handleChange}
                      error={Boolean(errors.location && touched.location)}
                      helperText={
                        Boolean(errors.location && touched.location) &&
                        errors.location
                      }
                      sx={{ ...textFieldStyle }}
                    />
                  </Box>

                  <Box>
                    <Typography sx={inputFieldLabelStyle}>Position*</Typography>
                    <TextField
                      type='text'
                      name='position'
                      id='position'
                      fullWidth
                      value={values.position}
                      onInput={(e: any) => {
                        e.target.value = getCapitalizedString(
                          e.target.value ?? ''
                        );
                      }}
                      onChange={handleChange}
                      error={Boolean(errors.position && touched.position)}
                      helperText={
                        Boolean(errors.position && touched.position) &&
                        errors.position
                      }
                      sx={{ ...textFieldStyle }}
                    />
                  </Box>

                  {/* <TextField
                    type='number'
                    name='no_of_sales_people'
                    id='no_of_sales_people'
                    label={`Number of sales people*`}
                    fullWidth
                    value={values.no_of_sales_people}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Box component={'img'} src={GroupIcon} />
                        </InputAdornment>
                      ),
                    }}
                    onChange={handleChange}
                    error={Boolean(
                      errors.no_of_sales_people && touched.no_of_sales_people
                    )}
                    helperText={
                      Boolean(
                        errors.no_of_sales_people && touched.no_of_sales_people
                      ) && errors.no_of_sales_people
                    }
                    sx={{ ...textFieldStyle }}
                  />

                  <TextField
                    type='tel'
                    name='office_number'
                    id='office_number'
                    label={`Office number*`}
                    onChange={handleChange}
                    value={values.office_number}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Box component={'img'} src={CallIcon} />
                        </InputAdornment>
                      ),
                    }}
                    error={Boolean(
                      errors.office_number && touched.office_number
                    )}
                    helperText={
                      Boolean(errors.office_number && touched.office_number) &&
                      errors.office_number
                    }
                    sx={{ ...textFieldStyle }}
                  />

                  <TextField
                    type='tel'
                    name='office_number_ext'
                    id='office_number_ext'
                    label={`Ext`}
                    onChange={handleChange}
                    value={values.office_number_ext}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Box component={'img'} src={CallIcon} />
                        </InputAdornment>
                      ),
                    }}
                    error={Boolean(
                      errors.office_number_ext && touched.office_number_ext
                    )}
                    helperText={
                      Boolean(
                        errors.office_number_ext && touched.office_number_ext
                      ) && errors.office_number_ext
                    }
                    sx={{ ...textFieldStyle }}
                  /> */}

                  <Box>
                    <Typography sx={inputFieldLabelStyle}>
                      Mobile Number*
                    </Typography>
                    <TextField
                      type='tel'
                      name='mobile_number'
                      id='mobile_number'
                      placeholder='Required to contact you via text message'
                      fullWidth
                      onChange={handleChange}
                      value={values.mobile_number}
                      error={Boolean(
                        errors.mobile_number && touched.mobile_number
                      )}
                      helperText={
                        Boolean(
                          errors.mobile_number && touched.mobile_number
                        ) && errors.mobile_number
                      }
                      sx={{ ...textFieldStyle }}
                    />
                  </Box>

                  <Box>
                    <Typography sx={inputFieldLabelStyle}>
                      Work Email*
                    </Typography>
                    <TextField
                      type='email'
                      name='office_email'
                      id='office_email'
                      fullWidth
                      onChange={handleChange}
                      value={values.office_email}
                      error={Boolean(
                        errors.office_email && touched.office_email
                      )}
                      helperText={
                        Boolean(errors.office_email && touched.office_email) &&
                        errors.office_email
                      }
                      sx={{ ...textFieldStyle }}
                    />
                  </Box>

                  <Box>
                    <Typography sx={inputFieldLabelStyle}>
                      KnoCard Brand Consultant (optional)
                    </Typography>
                    <TextField
                      type='text'
                      name='sales_rep'
                      id='sales_rep'
                      fullWidth
                      onChange={handleChange}
                      value={values.sales_rep}
                      error={Boolean(errors.sales_rep && touched.sales_rep)}
                      helperText={
                        Boolean(errors.sales_rep && touched.sales_rep) &&
                        errors.sales_rep
                      }
                      sx={{ ...textFieldStyle }}
                    />
                  </Box>

                  {/* <FormControl
                    fullWidth
                    error={Boolean(
                      errors.member_of_dealer_group &&
                        touched.member_of_dealer_group
                    )}
                    sx={{ ...textFieldStyle }}
                  >
                    <InputLabel id='member_of_dealer_group_label'>
                      {`Member of a dealer group?*`}
                    </InputLabel>
                    <Select
                      labelId='member_of_dealer_group_label'
                      id='member_of_dealer_group'
                      name='member_of_dealer_group'
                      value={values.member_of_dealer_group}
                      onChange={handleChange}
                      input={
                        <OutlinedInput
                          label={`Member of a dealer group?*`}
                          startAdornment={
                            <InputAdornment position='start'>
                              <Box component='img' src={GroupIcon} />
                            </InputAdornment>
                          }
                        />
                      }
                      MenuProps={{
                        disablePortal: true,
                        PaperProps: {
                          sx: {
                            zIndex: 1400,
                          },
                        },
                      }}
                    >
                      <MenuItem value={1}>Yes</MenuItem>
                      <MenuItem value={0}>No</MenuItem>
                    </Select>

                    {Boolean(
                      errors.member_of_dealer_group &&
                        touched.member_of_dealer_group
                    ) && (
                      <FormHelperText>
                        {errors.member_of_dealer_group}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl
                    fullWidth
                    error={Boolean(errors.contact_time && touched.contact_time)}
                    sx={{ ...textFieldStyle }}
                  >
                    <InputLabel id='contact_time_label'>
                      {`Best times to contact you*`}
                    </InputLabel>
                    <Select
                      labelId='contact_time_label'
                      id='contact_time'
                      name='contact_time'
                      value={values.contact_time}
                      onChange={handleChange}
                      input={
                        <OutlinedInput
                          label={`Best times to contact you*`}
                          startAdornment={
                            <InputAdornment position='start'>
                              <Box component='img' src={PersonIcon} />
                            </InputAdornment>
                          }
                        />
                      }
                      MenuProps={{
                        disablePortal: true,
                        PaperProps: {
                          sx: {
                            zIndex: 1400,
                          },
                        },
                      }}
                    >
                      {['9am - noon', 'noon - 4pm', '4pm - 8pm']?.map(
                        (item) => {
                          return (
                            <MenuItem value={item}>
                              <Box>{item}</Box>
                            </MenuItem>
                          );
                        }
                      )}
                    </Select>

                    {Boolean(errors.contact_time && touched.contact_time) && (
                      <FormHelperText>{errors.contact_time}</FormHelperText>
                    )}
                  </FormControl> */}

                  <Stack
                    direction='column'
                    spacing={2}
                    justifyContent={'flex-end'}
                  >
                    <LoadingButton
                      sx={{
                        minWidth: '100px',
                        borderColor: AppColors.blueColor,
                        backgroundColor: AppColors.blueColor,
                        color: '#ffffff',
                        // textTransform: 'unset',
                        fontWeight: '600',
                        '& span span': {
                          color: '#ffffff',
                        },
                      }}
                      type='submit'
                      loading={isSubmitting}
                      variant='outlined'
                    >
                      Book Demo
                    </LoadingButton>
                    {/* <LoadingButton
                      sx={{
                        minWidth: '80px',
                        color: AppColors.grayThreeColor,
                        textTransform: 'unset',
                        fontWeight: '600',
                        mt: '10px !important',
                      }}
                      type='reset'
                      variant='text'
                      onClick={() => {
                        resetForm();
                        onClose();
                      }}
                    >
                      Close
                    </LoadingButton> */}
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        {/* <DialogActions sx={{ paddingX: 3, justifyContent: 'center' }}>
          <Box
            component={'img'}
            src={'/static/images/knocard_auto_logo.png'}
            sx={{ height: '40px' }}
          ></Box>
        </DialogActions> */}
      </form>

      {videoUrl && (
        <VideoModel
          onClose={onCloseVideo}
          toggle={toggleVideo}
          videoUrl={videoUrl ?? ''}
          title=''
        />
      )}
    </Dialog>
  );
};

export default BookDemoFormModel;
